/**
 * GoalDocumentStore — the data-access wrapper for a goal's persisted documents.
 *
 * Strict layering: this is the ONLY goal-documents module that talks to the API,
 * and it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<GoalDocument>('goalDocuments')` + `api.identityProvider`). It
 * never imports ApiClient or calls fetch. The React hook
 * ({@link import('./useGoalKnowledge').useGoalKnowledge}) calls this store; pages
 * call the hook.
 *
 * Persistence model: each goal document is one document with id
 * `goaldoc_<uuid>` in the real 'goalDocuments' generic-store collection (DDB
 * pointer row + S3 body). Every renderable field (goalId, roomId, title,
 * fileName, kbObjectKey, ingested, ingestedAt, addedBy, addedAt) is mirrored
 * onto the DDB pointer row's index attributes — with {@link goalId} as the
 * query key — so {@link listByGoal} renders a goal's documents directly from
 * the pointer rows WITHOUT hydrating S3 bodies, exactly like the tasks/goals
 * stores.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { ListMetaInfoQuery } from '../../api';
import type { GoalDocument } from './goalKnowledge';
import { goalDocumentId } from './goalKnowledge';

export const GOAL_DOCUMENTS_COLLECTION = 'goalDocuments';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  goalId: 'goalId',
  roomId: 'roomId',
  title: 'title',
  fileName: 'fileName',
  kbObjectKey: 'kbObjectKey',
  ingested: 'ingested',
  ingestedAt: 'ingestedAt',
  addedBy: 'addedBy',
  addedAt: 'addedAt',
} as const;

/** Fields the caller supplies when recording an uploaded document. */
export interface GoalDocumentDraft {
  /** Human-readable title (falls back to the file name when blank). */
  title?: string;
  /** Original uploaded file name. */
  fileName: string;
  /** S3 object key the file was uploaded to under the goal-scoped KB prefix. */
  kbObjectKey: string;
}

export class GoalDocumentStore {
  private readonly docs: DocumentStore<GoalDocument>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<GoalDocument>(GOAL_DOCUMENTS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(doc: GoalDocument): StringMap {
    return {
      [ROW.goalId]: doc.goalId,
      [ROW.roomId]: doc.roomId ?? '',
      [ROW.title]: doc.title ?? '',
      [ROW.fileName]: doc.fileName ?? '',
      [ROW.kbObjectKey]: doc.kbObjectKey ?? '',
      [ROW.ingested]: doc.ingested ? 'true' : 'false',
      [ROW.ingestedAt]: doc.ingestedAt ?? '',
      [ROW.addedBy]: doc.addedBy ?? '',
      [ROW.addedAt]: doc.addedAt ?? '',
    };
  }

  /** Reconstructs a full GoalDocument from a pointer row (no S3 read). */
  private toDocument(row: StringMap): GoalDocument {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      goalId: row[ROW.goalId] ?? '',
      roomId: row[ROW.roomId] ?? '',
      title: row[ROW.title] ?? '',
      fileName: row[ROW.fileName] ?? '',
      kbObjectKey: row[ROW.kbObjectKey] ?? '',
      ingested: row[ROW.ingested] === 'true',
      ingestedAt: row[ROW.ingestedAt] || undefined,
      addedBy: row[ROW.addedBy] ?? '',
      addedAt: row[ROW.addedAt] ?? '',
    };
  }

  /**
   * Lists a goal's documents (most-recent first). Issues a goalId-filtered
   * listMetaInfo query (goalId is stored as an index attribute), then re-filters
   * client-side (collection + goalId) so the result is correct even if the
   * backend ignores the filter expression.
   */
  async listByGoal(goalId: string): Promise<GoalDocument[]> {
    if (!goalId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#goalId = :goalId',
      expressionAttributeNames: { '#goalId': ROW.goalId },
      expressionAttributeValues: { ':goalId': goalId },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === GOAL_DOCUMENTS_COLLECTION &&
          r[ROW.goalId] === goalId &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toDocument(r))
      .sort((a, b) => (b.addedAt ?? '').localeCompare(a.addedAt ?? ''));
  }

  /**
   * Records a newly-uploaded document for a goal (ingested = false). Seeds id,
   * addedBy (from identity) and addedAt, and returns the saved record.
   */
  async record(
    goalId: string,
    roomId: string,
    draft: GoalDocumentDraft,
  ): Promise<GoalDocument> {
    const identity = await this.api.identityProvider.getIdentity();
    const doc: GoalDocument = {
      id: goalDocumentId(),
      goalId,
      roomId: roomId ?? '',
      title: (draft.title ?? '').trim() || draft.fileName,
      fileName: draft.fileName,
      kbObjectKey: draft.kbObjectKey,
      ingested: false,
      addedBy: identity.alias,
      addedAt: new Date().toISOString(),
    };
    await this.docs.save(doc.id, doc, this.indexAttributes(doc));
    return doc;
  }

  /**
   * Marks all not-yet-ingested documents for a goal as ingested and stamps
   * ingestedAt. Called after a successful StartIngestion so the goal's docs
   * reflect that they are now indexed into the knowledge base. Already-ingested
   * docs are left untouched (their original ingestedAt is preserved). Returns
   * the refreshed document list.
   */
  async markGoalIngested(goalId: string): Promise<GoalDocument[]> {
    const docs = await this.listByGoal(goalId);
    const now = new Date().toISOString();
    return Promise.all(
      docs.map(async (d) => {
        if (d.ingested) return d;
        const next: GoalDocument = { ...d, ingested: true, ingestedAt: now };
        await this.docs.update(next.id, next, this.indexAttributes(next));
        return next;
      }),
    );
  }
}
